import type { RefObject } from 'react';
import { useEffect } from 'react';
import { useIntersectionObserver } from 'usehooks-ts';

/**
 * Tracks whether a `position: sticky` element is currently pinned at
 * `thresholdPx`, by observing a non-sticky 0-height sentinel rendered at the
 * sticky element's natural top.
 */
export const useIsPinned = (
  sentinelRef: RefObject<HTMLElement>,
  thresholdPx: number,
): boolean => {
  // Compute the offset as a single number before interpolating so a negative
  // threshold doesn't produce a double-negative (`--99px`) string, and so a
  // non-finite threshold doesn't produce `NaNpx`. Either would be rejected by
  // the IntersectionObserver constructor with a `DOMException`.
  const safeThresholdPx = Number.isFinite(thresholdPx) ? thresholdPx : 0;
  const topMarginPx = -(safeThresholdPx + 1);
  const { ref: observerRef, isIntersecting } = useIntersectionObserver({
    initialIsIntersecting: true,
    rootMargin: `${topMarginPx}px 0px 0px 0px`,
  });

  useEffect(() => {
    observerRef(sentinelRef.current);
    return () => observerRef(null);
  }, [sentinelRef, observerRef]);

  return !isIntersecting;
};

export default useIsPinned;
